/**
 * AdminSettingsPage - Admin-only settings and configuration.
 */
export function AdminSettingsPage() {
  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage admin configuration and preferences
        </p>
      </div>

      {/* Settings sections */}
      <div className="space-y-6">
        {/* General settings */}
        <SettingsSection title="General">
          <SettingRow
            label="Maintenance Mode"
            description="Temporarily disable the app for all users"
          >
            <Toggle disabled />
          </SettingRow>
          <SettingRow
            label="New Registrations"
            description="Allow new users to sign up"
          >
            <Toggle defaultChecked disabled />
          </SettingRow>
        </SettingsSection>

        {/* Email settings */}
        <SettingsSection title="Email">
          <SettingRow
            label="Welcome Emails"
            description="Send welcome email to new users"
          >
            <Toggle defaultChecked disabled />
          </SettingRow>
          <SettingRow
            label="Weekly Progress Reports"
            description="Send weekly progress reports to parents"
          >
            <Toggle defaultChecked disabled />
          </SettingRow>
        </SettingsSection>

        {/* Subscription settings */}
        <SettingsSection title="Subscriptions">
          <SettingRow
            label="Free Trial"
            description="Enable free trial for new users"
          >
            <Toggle disabled />
          </SettingRow>
          <SettingRow
            label="Trial Duration"
            description="Number of days for free trial"
          >
            <input
              type="number"
              defaultValue={7}
              disabled
              className="
                w-20 px-3 py-2 text-sm
                bg-gray-100 border border-gray-200 rounded-lg
                text-gray-500 cursor-not-allowed
              "
            />
          </SettingRow>
        </SettingsSection>

        {/* Danger zone */}
        <SettingsSection title="Danger Zone" variant="danger">
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <p className="text-sm text-red-600">
              Settings configuration is currently in development.
              Contact support for immediate configuration changes.
            </p>
          </div>
        </SettingsSection>
      </div>
    </div>
  );
}

interface SettingsSectionProps {
  title: string;
  children: React.ReactNode;
  variant?: "default" | "danger";
}

function SettingsSection({ title, children, variant = "default" }: SettingsSectionProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div
        className={`px-6 py-4 border-b ${
          variant === "danger"
            ? "border-red-200 bg-red-50"
            : "border-gray-200"
        }`}
      >
        <h2
          className={`text-lg font-semibold ${
            variant === "danger" ? "text-red-900" : "text-gray-900"
          }`}
        >
          {title}
        </h2>
      </div>
      <div className="p-6 space-y-6">{children}</div>
    </div>
  );
}

interface SettingRowProps {
  label: string;
  description: string;
  children: React.ReactNode;
}

function SettingRow({ label, description, children }: SettingRowProps) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      {children}
    </div>
  );
}

interface ToggleProps {
  defaultChecked?: boolean;
  disabled?: boolean;
}

function Toggle({ defaultChecked = false, disabled = false }: ToggleProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={`
        relative inline-flex h-6 w-11 flex-shrink-0
        rounded-full border-2 border-transparent
        transition-colors duration-200 ease-in-out
        ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
        ${defaultChecked ? "bg-magic-600" : "bg-gray-200"}
      `}
      role="switch"
      aria-checked={defaultChecked}
    >
      <span
        className={`
          pointer-events-none inline-block h-5 w-5
          transform rounded-full bg-white shadow ring-0
          transition duration-200 ease-in-out
          ${defaultChecked ? "translate-x-5" : "translate-x-0"}
        `}
      />
    </button>
  );
}

export default AdminSettingsPage;
